import { memo } from "react";
import { Box, Chip, Paper, Stack, Typography } from "@mui/material";
import { MarkEmailUnreadOutlined } from "@mui/icons-material";
import dayjs from "dayjs";
import { invitePatientDisplay } from "../helpers/inviteTaskHelpers";
import { usePendingInviteTasks } from "../hooks/usePendingInviteTasks";
import { InviteTaskActions } from "./InviteTaskActions";

/** Convite pendente: quem convidou, paciente, prazo e as ações. */
const PendingInviteCard = memo(function PendingInviteCard({ task }: { task: fhir5.Task }) {
  const deadline = task.restriction?.period?.end;
  return (
    <Paper
      variant="outlined"
      role="listitem"
      sx={{
        p: 1.5,
        borderLeft: "4px solid",
        borderLeftColor: "warning.main",
        borderRadius: "0 8px 8px 0",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 1.5, sm: 2 }}
        alignItems={{ sm: "center" }}
      >
        <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ minWidth: 0 }}>
              {invitePatientDisplay(task)}
            </Typography>
            <Chip size="small" color="warning" variant="outlined" label="Aguardando você" />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {task.requester?.display ? `Convite de ${task.requester.display}` : "Convite recebido"}
            {deadline ? ` · válido até ${dayjs(deadline).format("DD/MM HH:mm")}` : ""}
          </Typography>
        </Stack>
        <Box sx={{ flexShrink: 0, width: { xs: "100%", sm: "auto" } }}>
          <InviteTaskActions task={task} />
        </Box>
      </Stack>
    </Paper>
  );
});

/**
 * Convites (Tasks) aguardando resposta, no topo da tela de Teleconsultoria.
 * Some quando não há convite pendente (a lista de aceitos continua abaixo).
 */
export const PendingInviteTasksList = memo(function PendingInviteTasksList() {
  const pendingInvites = usePendingInviteTasks();
  if (!pendingInvites.length) return null;

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center">
        <MarkEmailUnreadOutlined color="warning" fontSize="small" aria-hidden />
        <Typography variant="subtitle1" fontWeight={700}>
          Convites para responder ({pendingInvites.length})
        </Typography>
      </Stack>
      <Stack spacing={1} role="list">
        {pendingInvites.map((task) => (
          <PendingInviteCard key={task.id} task={task} />
        ))}
      </Stack>
    </Stack>
  );
});
